import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { pricingScenarioService } from '../../services/financeService';
import { PricingScenario } from '../../types/finance';
import { FaSave, FaTrash, FaPlus, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';

const PriceCalculator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState<PricingScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentScenario, setCurrentScenario] = useState<PricingScenario>({
    name: '',
    product_name: '',
    cost_per_unit: 0,
    fixed_costs: 0,
    desired_margin: 0.3, // Default 30% margin
    recommended_price: 0,
    pricing_strategy: 'cost-plus',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadScenarios();
  }, [user, navigate]);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      const data = await pricingScenarioService.getAll();
      setScenarios(data);
    } catch (error) {
      console.error('Error loading pricing scenarios:', error);
      toast.error('Failed to load pricing scenarios');
    } finally {
      setLoading(false);
    }
  };

  const calculateRecommendedPrice = (scenario: PricingScenario): number => {
    const { cost_per_unit, fixed_costs, desired_margin } = scenario;
    
    // Simple cost-plus pricing formula
    // Price = Cost / (1 - Desired Margin)
    const totalCostPerUnit = cost_per_unit + (fixed_costs / 100); // Assuming 100 units for simplicity
    const price = totalCostPerUnit / (1 - desired_margin);
    
    return parseFloat(price.toFixed(2));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;
    
    // Parse numeric values
    if (['cost_per_unit', 'fixed_costs', 'desired_margin', 'competitor_price'].includes(name)) {
      parsedValue = parseFloat(value) || 0;
    }
    
    setCurrentScenario(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const recommended_price = calculateRecommendedPrice(currentScenario);
      const scenarioToSave = {
        ...currentScenario,
        user_id: user?.id,
        recommended_price
      };
      
      if (currentScenario.id) {
        await pricingScenarioService.update(currentScenario.id, scenarioToSave);
        toast.success('Pricing scenario updated successfully');
      } else {
        await pricingScenarioService.create(scenarioToSave);
        toast.success('Pricing scenario created successfully');
      }
      
      setShowForm(false);
      setCurrentScenario({
        name: '',
        product_name: '',
        cost_per_unit: 0,
        fixed_costs: 0,
        desired_margin: 0.3,
        recommended_price: 0,
        pricing_strategy: 'cost-plus',
      });
      loadScenarios();
    } catch (error) {
      console.error('Error saving pricing scenario:', error);
      toast.error('Failed to save pricing scenario');
    }
  };

  const handleEdit = (scenario: PricingScenario) => {
    setCurrentScenario(scenario);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this pricing scenario?')) {
      try {
        await pricingScenarioService.delete(id);
        toast.success('Pricing scenario deleted successfully');
        loadScenarios();
      } catch (error) {
        console.error('Error deleting pricing scenario:', error);
        toast.error('Failed to delete pricing scenario');
      }
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 relative">
      {/* Animated background elements */}
      <div className="absolute top-40 left-20 w-72 h-72 bg-blue-300/20 rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
      <div className="absolute top-20 right-20 w-72 h-72 bg-purple-300/20 rounded-full filter blur-3xl animate-blob animation-delay-4000"></div>
      <div className="absolute bottom-40 left-1/2 w-72 h-72 bg-pink-300/10 rounded-full filter blur-3xl animate-blob"></div>
      
      <div className="mb-6 relative z-10">
        <button
          onClick={() => navigate('/finance')}
          className="flex items-center px-4 py-2 rounded-lg bg-white/30 backdrop-blur-sm border border-white/40 text-blue-700 hover:bg-white/50 transition-all duration-200 shadow-sm"
        >
          <FaArrowLeft className="mr-2" /> Back to Financial Tools
        </button>
      </div>
      
      <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 mb-8">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-80"></div>
        <div className="absolute -right-20 top-0 w-60 h-60 bg-blue-300/10 rounded-full filter blur-3xl"></div>
        
        <div className="relative z-10">
          <h1 className="text-3xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Price Calculator</h1>
          <p className="text-gray-700 mb-8 text-lg">
            Determine the optimal price for your products or services based on costs and desired profit margins.
          </p>
          
          <div className="relative overflow-hidden backdrop-blur-sm bg-blue-600/10 border border-blue-300/30 rounded-xl p-6 mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-100/30 to-indigo-100/30 opacity-80"></div>
            
            <div className="relative z-10">
              <h2 className="text-xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">How it works</h2>
              <p className="mb-3 text-blue-900">
                This tool helps you calculate the right price for your products or services using the cost-plus pricing method.
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-center text-blue-800">
                  <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 mr-2"></span>
                  Enter your product's cost per unit (direct costs like materials and labor)
                </li>
                <li className="flex items-center text-blue-800">
                  <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 mr-2"></span>
                  Add any fixed costs (overhead expenses like rent, utilities, etc.)
                </li>
                <li className="flex items-center text-blue-800">
                  <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 mr-2"></span>
                  Set your desired profit margin as a decimal (e.g., 0.3 for 30%)
                </li>
                <li className="flex items-center text-blue-800">
                  <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 mr-2"></span>
                  The calculator will determine a recommended price that ensures your desired profit
                </li>
              </ul>
              <p className="text-blue-900">
                Save multiple pricing scenarios to compare different strategies and find what works best for your business.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {!showForm ? (
        <div className="mb-8 relative z-10">
          <button
            onClick={() => {
              setCurrentScenario({
                name: '',
                product_name: '',
                cost_per_unit: 0,
                fixed_costs: 0,
                desired_margin: 0.3,
                recommended_price: 0,
                pricing_strategy: 'cost-plus',
              });
              setShowForm(true);
            }}
            className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 flex items-center transition-all duration-200 group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <span className="relative z-10 flex items-center">
              <FaPlus className="mr-2 group-hover:rotate-90 transition-transform duration-200" /> Create New Pricing Scenario
            </span>
          </button>
        </div>
      ) : (
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-80"></div>
          <div className="absolute top-0 left-0 w-40 h-40 bg-blue-300/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-purple-300/10 rounded-full filter blur-3xl"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-5 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              {currentScenario.id ? 'Edit Pricing Scenario' : 'Create New Pricing Scenario'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    Scenario Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={currentScenario.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                    placeholder="e.g., Summer Product Line"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    Product/Service Name
                  </label>
                  <input
                    type="text"
                    name="product_name"
                    value={currentScenario.product_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                    placeholder="e.g., Handmade Soap"
                    required
                  />
                </div>
              </div>
            
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    Cost Per Unit ($)
                  </label>
                  <input
                    type="number"
                    name="cost_per_unit"
                    value={currentScenario.cost_per_unit}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                    placeholder="5.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    Fixed Costs ($)
                  </label>
                  <input
                    type="number"
                    name="fixed_costs"
                    value={currentScenario.fixed_costs}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                    placeholder="100.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    Desired Profit Margin
                  </label>
                  <input
                    type="number"
                    name="desired_margin"
                    value={currentScenario.desired_margin}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                    placeholder="0.30"
                    min="0"
                    max="0.99"
                    step="0.01"
                    required
                  />
                  <span className="text-xs text-blue-600 mt-1 block">
                    Enter as decimal (e.g., 0.3 for 30%)
                  </span>
                </div>
              </div>
            
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    Competitor's Price ($) (Optional)
                  </label>
                  <input
                    type="number"
                    name="competitor_price"
                    value={currentScenario.competitor_price || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                    placeholder="12.99"
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    Pricing Strategy
                  </label>
                  <select
                    name="pricing_strategy"
                    value={currentScenario.pricing_strategy}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                    required
                  >
                    <option value="cost-plus">Cost-Plus Pricing</option>
                    <option value="value-based">Value-Based Pricing</option>
                    <option value="competitive">Competitive Pricing</option>
                    <option value="premium">Premium Pricing</option>
                    <option value="economy">Economy Pricing</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                  Notes (Optional)
                </label>
                <input
                  type="text"
                  name="notes"
                  value={currentScenario.notes || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-colors text-gray-800"
                  placeholder="Any additional information about this pricing scenario"
                />
              </div>
            
              <div className="flex flex-wrap justify-between mt-8 mb-6">
                <div className="flex flex-wrap gap-3 mb-3 sm:mb-0">
                  <button
                    type="submit"
                    className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 flex items-center transition-all duration-200 group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    <span className="relative z-10 flex items-center">
                      <FaSave className="mr-2 group-hover:scale-110 transition-transform duration-200" /> {currentScenario.id ? 'Update' : 'Save'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="relative overflow-hidden bg-white/50 backdrop-blur-sm text-gray-700 px-5 py-2.5 rounded-xl border border-white/40 shadow-sm hover:bg-white/70 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
                
                {currentScenario.id && (
                  <button
                    type="button"
                    onClick={() => handleDelete(currentScenario.id!)}
                    className="relative overflow-hidden bg-gradient-to-r from-red-500 to-pink-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl flex items-center transition-all duration-200 group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    <span className="relative z-10 flex items-center">
                      <FaTrash className="mr-2 group-hover:scale-110 transition-transform duration-200" /> Delete
                    </span>
                  </button>
                )}
              </div>
              
              {currentScenario.cost_per_unit > 0 && (
                <div className="relative overflow-hidden backdrop-blur-sm bg-green-100/50 border border-green-300/50 rounded-xl shadow-sm p-5 mt-4">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50/70 to-teal-50/70 opacity-90"></div>
                  <div className="absolute -right-10 bottom-0 w-40 h-40 bg-green-300/20 rounded-full filter blur-3xl"></div>
                  
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600">Price Preview</h3>
                    <p className="text-gray-700 text-lg">
                      Based on your inputs, the recommended price is: 
                      <span className="font-bold text-green-600 text-xl">
                        ${calculateRecommendedPrice(currentScenario).toFixed(2)}
                      </span>
                    </p>
                    <p className="text-sm text-green-700 mt-2 font-medium">
                      This price will give you a {(currentScenario.desired_margin * 100).toFixed(0)}% profit margin.
                    </p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      
      {scenarios.length > 0 ? (
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-80"></div>
          <div className="absolute top-0 left-1/3 w-40 h-40 bg-blue-300/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/3 w-40 h-40 bg-purple-300/10 rounded-full filter blur-3xl"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-5 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Your Pricing Scenarios</h2>
            
            <div className="relative overflow-hidden rounded-xl shadow-sm bg-white/30 backdrop-blur-sm border border-white/40">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-blue-100/30">
                  <thead className="bg-gradient-to-r from-blue-50/70 to-purple-50/70">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                        Cost/Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                        Margin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                        Recommended Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                        Strategy
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100/20">
                    {scenarios.map((scenario) => (
                      <tr key={scenario.id} className="hover:bg-blue-50/30 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-700">
                          {scenario.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          {scenario.product_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          <span className="px-3 py-1 rounded-lg bg-blue-50/70 text-blue-700">
                            ${scenario.cost_per_unit.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 rounded-lg bg-purple-50/70 text-purple-700 font-medium">
                            {(scenario.desired_margin * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-bold">
                          <span className="px-3 py-1 rounded-lg bg-green-50/70 text-green-700">
                            ${scenario.recommended_price.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-indigo-700">
                          {scenario.pricing_strategy.replace('-', ' ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleEdit(scenario)}
                              className="text-blue-600 hover:text-blue-900 p-1.5 rounded-lg transition-colors duration-200 hover:bg-blue-100/50 flex items-center"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(scenario.id!)}
                              className="text-red-600 hover:text-red-800 p-1.5 rounded-lg transition-colors duration-200 hover:bg-red-100/50 flex items-center"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : (
        !showForm && (
          <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-10 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 opacity-70"></div>
            <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-40 h-40 bg-purple-300/20 rounded-full filter blur-3xl"></div>
            <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-40 h-40 bg-blue-300/20 rounded-full filter blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 border border-white/40 flex items-center justify-center shadow-md">
                <FaPlus className="text-blue-600 text-3xl" />
              </div>
              <p className="text-gray-700 text-lg mb-6">You haven't created any pricing scenarios yet.</p>
              <button
                onClick={() => setShowForm(true)}
                className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center transition-all duration-200 group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                <span className="relative z-10 flex items-center">
                  <FaPlus className="mr-2 group-hover:rotate-90 transition-transform duration-200" /> Create Your First Pricing Scenario
                </span>
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default PriceCalculator;
